import { useState } from 'react';
import { api } from '../../../lib/api.js';

const TYPES = [
  { value: 'idea', label: 'Idea', hint: 'A suggestion to improve the dashboard or workflow.' },
  { value: 'issue', label: 'Issue', hint: 'Something broken, confusing, or wrong.' },
  { value: 'other', label: 'Other', hint: 'Anything else worth saying.' },
];

// Editor-level feedback shortcut. Distinct from per-article ProblemReport — this
// is for ideas / issues about the dashboard itself, routed to EditorFeedback in
// Mongo.
export default function FeedbackButton({ isoDate, view }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('idea');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  function reset() {
    setMessage('');
    setError(null);
    setDone(false);
    setSubmitting(false);
    setType('idea');
  }

  function close() {
    setOpen(false);
    reset();
  }

  async function onSubmit(e) {
    e?.preventDefault?.();
    const trimmed = message.trim();
    if (!trimmed) {
      setError('Add a short note.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/editor-feedback', {
        type,
        message: trimmed,
        isoDate,
        view,
      });
      setDone(true);
      setMessage('');
    } catch (err) {
      setError(err.message || 'Submit failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-5 z-30 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-hxd-ink hover:bg-hxd-orange text-white font-display text-[11px] font-bold uppercase tracking-display-md shadow-lg transition-colors"
        aria-label="Open feedback form"
      >
        <span aria-hidden="true">💬</span>
        Feedback
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div
            className="bg-white w-full max-w-md rounded border border-hxd-border shadow-xl"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-hxd-border-soft">
              <div>
                <div className="font-display text-[10px] font-bold tracking-display-md uppercase text-hxd-orange">
                  Dashboard
                </div>
                <div className="font-display text-[16px] font-bold text-hxd-ink">Feedback</div>
              </div>
              <button
                type="button"
                onClick={close}
                className="hxd-btn hxd-btn-ghost hxd-btn-sm"
                aria-label="Close feedback form"
              >
                ✕
              </button>
            </div>

            <div className="px-5 py-4">
              {done ? (
                <div className="py-6 text-center">
                  <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-ok mb-2">
                    Thanks — logged
                  </div>
                  <p className="text-[13px] text-hxd-ink mb-4">
                    Your note made it to the team queue.
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={reset}
                      className="hxd-btn hxd-btn-ghost hxd-btn-sm"
                    >
                      Send another
                    </button>
                    <button
                      type="button"
                      onClick={close}
                      className="hxd-btn hxd-btn-primary hxd-btn-sm"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit}>
                  <div className="mb-3">
                    <label className="hxd-label">Type</label>
                    <div className="flex flex-wrap gap-2">
                      {TYPES.map((opt) => {
                        const active = type === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setType(opt.value)}
                            className={
                              active
                                ? 'px-3 py-1.5 rounded-[3px] border bg-hxd-orange text-white border-hxd-orange text-[12px] font-bold'
                                : 'px-3 py-1.5 rounded-[3px] border bg-white text-hxd-ink border-hxd-border text-[12px] font-bold hover:border-hxd-orange'
                            }
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-hxd-muted mt-1.5">
                      {TYPES.find((t) => t.value === type)?.hint}
                    </p>
                  </div>

                  <div className="mb-3">
                    <label className="hxd-label">Your note</label>
                    <textarea
                      className="hxd-input min-h-[110px]"
                      rows={5}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      autoFocus
                      maxLength={2000}
                    />
                    <div className="text-[10px] text-hxd-muted text-right mt-1">
                      {message.length}/2000
                    </div>
                  </div>

                  {error && <div className="text-[12px] text-hxd-err mb-2">{error}</div>}

                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={close}
                      className="hxd-btn hxd-btn-ghost hxd-btn-sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || !message.trim()}
                      className="hxd-btn hxd-btn-primary hxd-btn-sm"
                    >
                      {submitting ? 'Sending…' : 'Send feedback'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
